import moment from "moment";
import { API_DATE } from "../Shared/formatters";
import { PENALTY_TYPES, hoursValue } from "./penaltiesData";

// Column mapping + checks for Pay Inputs > Penalties > Import. Same matching
// rules as the Leave import: a column is matched by its header against the
// field label + aliases, then by its values. Rows that pass are checked
// again by POST payroll/deduction/penalty/import/validate/ before the
// preview, and the import API does the final checks (employee, payroll
// cycle, duplicates, payroll lock). Penalty types come from
// GET payroll/deduction/penalty/types/ (PENALTY_TYPES if that fails).

export { applyServerChecks } from "../Shared/importMapping";

export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const IMPORT_ACCEPT = ".xlsx,.xls,.csv";

export const IMPORT_FIELDS = [
  {
    key: "employee_code",
    label: "Employee ID",
    required: true,
    aliases: ["employee id", "employee code", "emp id", "emp code", "emp no", "employee no", "employee number", "staff id", "staff code"],
  },
  {
    key: "date",
    label: "Date",
    required: true,
    aliases: ["date", "penalty date", "attendance date", "day", "dt"],
  },
  {
    key: "penalty_type",
    label: "Penalty Type",
    required: true,
    aliases: ["penalty type", "type", "deduction type", "penalty"],
  },
  {
    key: "basis",
    label: "Basis",
    required: false,
    aliases: ["basis", "penalty basis", "calculation basis", "unit"],
  },
  {
    key: "quantity",
    label: "Penalty Qty",
    required: false,
    aliases: ["penalty qty", "penalty quantity", "quantity", "qty", "hours", "shifts", "late hours"],
  },
  {
    key: "rate",
    label: "Rate (per unit)",
    required: false,
    aliases: ["rate", "rate per unit", "unit rate", "per day", "per hour", "rate per hour", "rate per day"],
  },
  {
    key: "payment_factor",
    label: "Factor",
    required: false,
    aliases: ["factor", "payment factor", "multiplier"],
  },
  {
    key: "amount",
    label: "Amount",
    required: false,
    aliases: ["amount", "penalty amount", "deduction amount", "fixed amount"],
  },
  {
    key: "shift_name",
    label: "Shift",
    required: false,
    aliases: ["shift", "shift name"],
  },
];

// The Penalty Type line lists the types from the server, or PENALTY_TYPES.
export const importGuidelines = (types = PENALTY_TYPES) => [
  "Use the provided template. Columns can be in any order and renamed — check the matches in the Map Columns step.",
  "Employee ID is the employee code (e.g. VST1005).",
  "Date format: DD MMM YYYY (e.g. 15 Oct 2026), DD-MM-YYYY or YYYY-MM-DD.",
  `Penalty Type is one of ${types.join(", ")}. Basis is Shift, Hours or Amount (blank = Shift).`,
  "Shift / Hours basis: Penalty Qty and Rate are required (hours as HH:MM or decimal), Factor blank = 1. The amount is calculated by the system: Penalty Qty × Rate × Factor.",
  "Amount basis: only Amount is needed (a fixed deduction). Amount is ignored for Shift / Hours rows.",
  "Rows go into the payroll cycle chosen on this step, or each row's own cycle when left as Automatic.",
  "One row per employee, date and penalty type. Maximum file size: 5 MB.",
];

const BASIS_VALUES = { shift: "shift", shifts: "shift", day: "shift", days: "shift", hours: "hours", hour: "hours", hrs: "hours", hourly: "hours", amount: "amount", fixed: "amount", fixedamount: "amount" };

const normalize = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const words = (value) =>
  String(value ?? "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

// Dice coefficient on letter pairs: 1 = identical, 0 = nothing shared.
const pairSimilarity = (a, b) => {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const pairs = (text) => {
    const list = [];
    for (let i = 0; i < text.length - 1; i += 1) list.push(text.slice(i, i + 2));
    return list;
  };
  const left = pairs(a);
  const pool = pairs(b);
  const total = left.length + pool.length;
  let shared = 0;
  left.forEach((pair) => {
    const at = pool.indexOf(pair);
    if (at !== -1) {
      shared += 1;
      pool.splice(at, 1);
    }
  });
  return (2 * shared) / total;
};

// How well a header names a field, 0..1.
const headerScore = (header, field) => {
  const name = normalize(header);
  if (!name) return 0;
  const headerWords = words(header);
  let best = 0;
  [field.label, ...field.aliases].forEach((candidate) => {
    const target = normalize(candidate);
    if (name === target) {
      best = Math.max(best, 1);
      return;
    }
    const targetWords = words(candidate);
    if (targetWords.length && targetWords.every((word) => headerWords.includes(word))) {
      best = Math.max(best, 0.9 - 0.02 * (headerWords.length - targetWords.length));
    }
    best = Math.max(best, pairSimilarity(name, target) * 0.85);
  });
  return best;
};

const HEADER_MATCH_MIN = 0.6;
const CONTENT_MATCH_MIN = 0.6;

const isBlankCell = (value) =>
  value === null || value === undefined || String(value).trim() === "";

const DATE_FORMATS = ["DD MMM YYYY", "D MMM YYYY", API_DATE, "DD-MM-YYYY", "DD/MM/YYYY"];

export const parseDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format(API_DATE);
  }
  const parsed = moment(String(value ?? "").trim(), DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format(API_DATE) : null;
};

// `types`: the penalty types from the server, or PENALTY_TYPES.
export const matchPenaltyType = (value, types = PENALTY_TYPES) =>
  types.find((type) => normalize(type) === normalize(value)) ?? null;

export const matchBasis = (value) => BASIS_VALUES[normalize(value)] ?? null;

// Fields that can be recognised from their values alone.
const contentRules = (types) => ({
  date: (value) => Boolean(parseDate(value)),
  penalty_type: (value) => Boolean(matchPenaltyType(value, types)),
  basis: (value) => Boolean(matchBasis(value)),
});

const contentScore = (rows, index, test) => {
  const sample = rows
    .map(({ cells }) => cells[index])
    .filter((value) => !isBlankCell(value))
    .slice(0, 50);
  if (!sample.length) return 0;
  return sample.filter((value) => test(value)).length / sample.length;
};

// { [columnIndex]: fieldKey }. Each field and each column is used at most
// once; the strongest header matches win first, then remaining fields are
// filled from column contents.
export const autoMapColumns = (headers, rows = [], types = PENALTY_TYPES) => {
  const CONTENT_RULES = contentRules(types);
  const mapping = {};
  const usedFields = new Set();
  const usedColumns = new Set();

  const assign = (candidates, minimum) => {
    candidates
      .filter((candidate) => candidate.score >= minimum)
      .sort((a, b) => b.score - a.score)
      .forEach(({ index, key }) => {
        if (usedFields.has(key) || usedColumns.has(index)) return;
        mapping[index] = key;
        usedFields.add(key);
        usedColumns.add(index);
      });
  };

  const byHeader = [];
  headers.forEach((header, index) => {
    IMPORT_FIELDS.forEach((field) => {
      byHeader.push({ index, key: field.key, score: headerScore(header, field) });
    });
  });
  assign(byHeader, HEADER_MATCH_MIN);

  const byContent = [];
  headers.forEach((header, index) => {
    if (usedColumns.has(index)) return;
    Object.entries(CONTENT_RULES).forEach(([key, test]) => {
      if (usedFields.has(key)) return;
      byContent.push({ index, key, score: contentScore(rows, index, test) });
    });
  });
  assign(byContent, CONTENT_MATCH_MIN);

  return mapping;
};

export const missingRequiredFields = (mapping) => {
  const mapped = new Set(Object.values(mapping));
  return IMPORT_FIELDS.filter((field) => field.required && !mapped.has(field.key));
};

// First non-empty row is the header row; fully blank rows are skipped.
// Data rows are numbered 1, 2, 3... (header and blank rows not counted).
export const splitSheetRows = (sheetRows) => {
  const isBlank = (row) => !row || row.every(isBlankCell);
  const headerIndex = sheetRows.findIndex((row) => !isBlank(row));
  if (headerIndex === -1) return { headers: [], rows: [] };
  const headers = sheetRows[headerIndex].map((cell) => String(cell ?? "").trim());
  const rows = [];
  sheetRows.slice(headerIndex + 1).forEach((cells) => {
    if (!isBlank(cells)) rows.push({ rowNumber: rows.length + 1, cells });
  });
  return { headers, rows };
};

const toNumber = (value) =>
  isBlankCell(value) ? NaN : Number(String(value).replace(/,/g, "").trim());

// Checks every data row against the mapping. Returns
// { rowNumber, raw: { [fieldKey]: raw }, errors: [string], row } where `row`
// is the API record (plus payable_amount for the preview) for valid rows.
const rowKey = (employeeCode, date, penaltyType) => `${employeeCode.toLowerCase()}|${date}|${penaltyType}`;

// Keys of the valid rows other than `exceptRowNumber`, for checkImportRow's
// duplicate check when one row is re-checked from the Edit panel.
export const takenRowKeys = (results, exceptRowNumber) =>
  new Set(
    results
      .filter((result) => result.row && result.rowNumber !== exceptRowNumber)
      .map(({ row }) => rowKey(row.employee_id, row.date, row.penalty_type)),
  );

// A row repeating an earlier row's employee, date and penalty type is a
// duplicate (the first one stays valid).
export const validateImportRows = (rows, mapping, types = PENALTY_TYPES) => {
  const seen = new Set();
  return rows.map(({ rowNumber, cells }) => {
    const raw = {};
    Object.entries(mapping).forEach(([index, key]) => {
      raw[key] = cells[Number(index)];
    });
    return checkImportRow(rowNumber, raw, types, seen);
  });
};

// Checks one row's values ({ [fieldKey]: value }). Also used to re-check a
// row corrected in the preview's Edit panel. `seen`: optional Set of
// "employee|date|type" keys already taken by other rows; a row whose key is
// in it gets the duplicate error, and its own key is added to it.
export const checkImportRow = (rowNumber, raw, types = PENALTY_TYPES, seen = null) => {
  const errors = [];
  const label = (key) => IMPORT_FIELDS.find((field) => field.key === key).label;

  const employeeCode = String(raw.employee_code ?? "").trim();
  if (!employeeCode) errors.push("Employee ID is required");

  let date = null;
  if (isBlankCell(raw.date)) errors.push("Date is required");
  else {
    date = parseDate(raw.date);
    if (!date) errors.push("Invalid date format");
  }

  let penaltyType = null;
  if (isBlankCell(raw.penalty_type)) errors.push("Penalty Type is required");
  else {
    penaltyType = matchPenaltyType(raw.penalty_type, types);
    if (!penaltyType) errors.push(`Penalty Type "${raw.penalty_type}" is not valid`);
  }

  let basis = "shift";
  if (!isBlankCell(raw.basis)) {
    basis = matchBasis(raw.basis);
    if (!basis) errors.push(`Basis "${raw.basis}" is not valid`);
  }

  const numeric = (key, { required }) => {
    if (isBlankCell(raw[key])) {
      if (required) errors.push(`${label(key)} is required`);
      return null;
    }
    const number = toNumber(raw[key]);
    if (!Number.isFinite(number)) {
      errors.push(`${label(key)} must be a numeric value`);
      return null;
    }
    if (number < 0) errors.push(`${label(key)} cannot be negative`);
    return number;
  };

  const record = {};
  let quantityNumber = 0;
  if (basis === "hours") {
    if (isBlankCell(raw.quantity)) errors.push("Penalty Qty is required");
    else {
      // Time cells arrive as "HH:MM" (see readSheet in PenaltyImport.js).
      const text = String(raw.quantity).trim();
      quantityNumber = hoursValue(text);
      if (quantityNumber === null) errors.push("Penalty Qty must be HH:MM or decimal hours below 24");
      // "0:30" / "00:30:00" are sent as "00:30"; decimal hours as typed.
      else record.quantity = text.includes(":") ? text.split(":").slice(0, 2).map((part) => part.padStart(2, "0")).join(":") : text;
    }
  } else if (basis === "shift") {
    const value = numeric("quantity", { required: true });
    if (value != null) { quantityNumber = value; record.quantity = value; }
  }
  const amountBasis = basis === "amount";
  // Shift / Hours: quantity, rate and factor (the server calculates the
  // amount). Amount: just the amount. Cells for the other basis are ignored.
  const rate = amountBasis ? null : numeric("rate", { required: true });
  const factor = amountBasis ? null : numeric("payment_factor", { required: false });
  const amount = amountBasis ? numeric("amount", { required: true }) : null;
  if (amountBasis) {
    if (amount != null) record.amount = amount;
  } else {
    if (rate != null) record.rate = rate;
    record.factor = factor ?? 1;
  }

  if (seen && employeeCode && date && penaltyType) {
    const key = rowKey(employeeCode, date, penaltyType);
    if (seen.has(key)) errors.push("Duplicate row in the file for this employee, date and penalty type");
    seen.add(key);
  }

  const shiftName = String(raw.shift_name ?? "").trim();
  return {
    rowNumber,
    raw,
    errors,
    row: errors.length
      ? null
      : {
          employee_id: employeeCode,
          date,
          penalty_type: penaltyType,
          basis,
          ...record,
          ...(shiftName ? { shift: shiftName } : {}),
          // Browser estimate until the validate API answers; never sent.
          payable_amount: amountBasis ? amount : quantityNumber * (rate || 0) * (factor ?? 1),
        },
  };
};

// Template rows (header + three examples) for the downloadable .xlsx. The
// examples use the server's penalty types when they are loaded.
export const importTemplateRows = (types = PENALTY_TYPES) => {
  const pick = (wanted, index) => matchPenaltyType(wanted, types) ?? types[index] ?? types[0] ?? wanted;
  return [
    IMPORT_FIELDS.map((field) => field.label),
    ["VST1005", moment().startOf("month").format("DD MMM YYYY"), pick("Late Entry", 0), "Hours", "00:30", 100, 1, "", "General SHIFT"],
    ["VST1031", moment().startOf("month").add(1, "day").format("DD MMM YYYY"), pick("Absent", 1), "Shift", 1, 800, "", "", ""],
    ["VST1044", moment().startOf("month").add(2, "day").format("DD MMM YYYY"), pick("Break Penalty", 2), "Amount", "", "", "", 150, ""],
  ];
};
